/**
 * Searching many Containers at once (Alan, 2026-09-30: "可以批量的搜索柜子一次性的搜索
 * 很多"). The server answers it - a row matches when ANY term does
 * (functions/src/dashboard-query.ts, dashboardSearchTerms) - and this is the same
 * reading of the typed text, so the page can say which of the numbers it was given
 * were not found. The two share no code; tests/fixtures/dashboard-search-list-cases.json
 * pins them to one table.
 *
 * A phrase stays a phrase. The list reading is taken only when the text is
 * unmistakably a list: it spans lines or tabs, or every space/comma separated piece
 * looks like a container number (a search box flattens a pasted column's line breaks
 * to spaces, which is why the second reading exists). Commas alone never split.
 */

const LINE_BREAKS = /[\n\r\t]+/u;
const LIST_SEPARATORS = /[\s,;\uFF0C\uFF1B\u3001]+/u;
const CONTAINER_TOKEN = /^[A-Z]{4}\d{4,7}$/u;

export function dashboardSearchListTerms(search) {
  const text = String(search ?? '').trim().toUpperCase();
  if (!text) return [];
  const lines = text.split(LINE_BREAKS).map((part) => part.trim()).filter(Boolean);
  if (lines.length >= 2) return [...new Set(lines)];
  const tokens = text.split(LIST_SEPARATORS).filter(Boolean);
  if (tokens.length >= 2 && tokens.every((token) => CONTAINER_TOKEN.test(token))) {
    return [...new Set(tokens)];
  }
  return [text];
}

/**
 * What a list search found: null when the text is not a list (a phrase has no
 * "missing"). A number is found when a loaded Container's OWN number contains it -
 * another row's paired empty no longer counts (Alan, 2026-10-14: EITU1473254 read as
 * found because it was OOCU6300773's 配空柜号).
 */
export function batchSearchReport({ search, rows }) {
  const terms = dashboardSearchListTerms(search);
  if (terms.length < 2) return null;
  const numbers = (Array.isArray(rows) ? rows : []).map((row) => String(row?.container_no ?? '').trim().toUpperCase())
    .filter(Boolean);
  // Whole or partial, a term is looked for in the Containers' OWN numbers only; "contains" keeps a number typed
  // with a suffix ("SEKU6504656SH") findable by its real number.
  const missing = terms.filter((term) => !numbers.some((number) => number.includes(term)));
  return { total: terms.length, found: terms.length - missing.length, missing };
}

export function batchSearchText(report, locale = 'zh') {
  if (!report) return '';
  const zh = String(locale).toLowerCase().startsWith('zh');
  if (!report.missing.length) {
    return zh ? `搜索 ${report.total} 个柜号，全部找到` : `Searched ${report.total} numbers - all found`;
  }
  const shown = report.missing.slice(0, 12).join(zh ? '、' : ', ');
  const more = report.missing.length > 12 ? (zh ? ` 等 ${report.missing.length} 个` : ` and ${report.missing.length - 12} more`) : '';
  return zh
    ? `搜索 ${report.total} 个柜号：找到 ${report.found}，没找到 ${report.missing.length}：${shown}${more}`
    : `Searched ${report.total} numbers: ${report.found} found, ${report.missing.length} not found: ${shown}${more}`;
}
